/**
 * This component contents a list of pill and swiper to called from GuidedNavigation
 *
 * @module views/components/PillCarousel
 * @memberof -Common
 */
import './PillCarousel.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import DismissiblePill from '@ulta/modules/DismissiblePill/DismissiblePill';
import SelectablePill from '@ulta/modules/SelectablePill/SelectablePill';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import { FilterPill } from '../FilterPill/FilterPill';

/**
 * Represents a PillCarousel component
 * @method
 * @param {PillCarouselProps} props - React properties passed from composition
 * @returns PillCarousel
 */

export const PillCarousel = function( props ){
  const {
    dataCapture,
    items,
    onPillClick,
    nextClickAction,
    previousClickAction,
    carouselAccessibility,
    previousAccessibility,
    nextAccessibility,
    carouselDetailsAccessibility,
    loading,
    onClearClick,
    clearAction,
    hasClearButton,
    pillType
  } = props;
  const pillCarouselRef = useRef( null );

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const _railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  const _railADAMeta = {
    carouselAccessibility: carouselUtils.handleTokenizedString( carouselAccessibility ),
    carouselDetailsAccessibility: carouselUtils.handleTokenizedString( carouselDetailsAccessibility )
  };

  return (
    <div
      className='PillCarousel'
      ref={ pillCarouselRef }
    >
      <Rail
        railRef={ pillCarouselRef }
        id={ railUuid }
        items={ items }
        previousClickAction={ previousClickAction }
        nextClickAction={ nextClickAction }
        dataCapture={ dataCapture }
        _railADAmeta={ _railADAMeta }
        _railMeta={ _railMeta }
        useObserver={ true }
        useObserveSlideChildren={ true }
        spaceBetween={ 8 }
        sliderPerViewXL={ 'auto' }
        slidesPerGroupXL={ 4 }
        sliderPerViewLG={ 'auto' }
        slidesPerGroupLG={ 4 }
        sliderPerViewMD={ 'auto' }
        slidesPerGroupMD={ 3 }
        sliderPerViewSM={ 'auto' }
        slidesPerGroupSM={ 5 }
      >
        { hasItems( items ) && items.map( ( item, index ) => {
          const type = item.pillType || pillType;
          return (
            <Slide key={ index }
              className='SwiperWidth'
            >
              { PILL_TYPES.FILTER === type && <FilterPill { ...spreadProps( { ...item } ) } /> }

              { PILL_TYPES.PILL === type && (
                <SelectablePill
                  key={ item.name }
                  message={ item.name }
                  onClick={ onPillClick || item.onPillClick }
                  selectAction={ item.selectAction }
                />
              ) }

              { PILL_TYPES.DISMISSIBLE === type && (
                <DismissiblePill
                  itemText={ item.title || item.label }
                  key={ item.id }
                  colorCode={ item.colorCode }
                  loading={ loading }
                  colorImage={ item.colorImage }
                  onClick={ () => onPillClick( item ) }
                  ariaLabel={ item.removeFilterAccessibility }
                  rating={ item.id }
                  groupType={ item.groupType }
                />
              ) }
            </Slide>
          );
        } ) }
        { hasClearButton &&
          <Slide className='PillCarousel--ClearButton SwiperWidth'>
            <span className=''
              data-test='reset-btn-mobile'
            >
              <Button
                action={ clearAction }
                likeLink
                label={ clearAction.label }
                onClick={ onClearClick }
                disabled={ loading }
              />
            </span>
          </Slide>
        }
      </Rail>
    </div>
  );
};

/**
 * Pill types
 * @constant {object}
 * @default
 */
export const PILL_TYPES = {
  PILL: 'PILL',
  DISMISSIBLE: 'DISMISSIBLE',
  FILTER: 'FILTER'
};

/**
  * Property type definitions
  * @typedef PillCarouselProps
  * @type {object}
  * @property {array} items - This is array of items
  * @property {boolean} displayArrows - flag to display swiper Pagination
  * @property {function} onPillClick - function to handle on pill click
  * @property {string} carouselAccessibility - This is the ADA text for carousel
  * @property {string} previousAccessibility - This is the ADA text for carousel prev pagination button
  * @property {string} nextAccessibility - This is the ADA text for carousel next pagination button
  */

export const propTypes =  {
  items: PropTypes.arrayOf( PropTypes.shape(
    {
      name: PropTypes.string,
      selected: PropTypes.bool,
      selectAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } )
    }
  ) ),
  displayArrows: PropTypes.bool,
  onPillClick: PropTypes.func,
  carouselAccessibility: PropTypes.string,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string
};


PillCarousel.propTypes = propTypes;

export default PillCarousel;
